import {format, setHours, setMinutes} from 'date-fns';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useMediaQuery} from '../../../../shared/services/useMediaQuery';
import {
  formatDisplayValue,
  parseDateString,
  parseTimeString,
} from '../../usecase/clickToEditDateInputUtils';
import {ModalDateInputPresenter} from './Presenter/ModalDateInputPresenter';

type Props = {
  value: string | undefined;
  onSave: (value: string | undefined) => void;
  disabled?: boolean;
  withTime?: boolean;
  error?: string;
  /** Inputに適用するクラス名。幅の指定などに使用。指定しない場合はflex-1が適用される */
  className?: string;
};

export const ModalDateInputContainer: React.FC<Props> = ({
  value,
  onSave,
  disabled = false,
  withTime = true,
  error,
  className,
}) => {
  const [inputText, setInputText] = useState(
    formatDisplayValue(value, withTime),
  );
  const [calendarOpened, setCalendarOpened] = useState(false);
  const [calendarDate, setCalendarDate] = useState<Date | null>(
    value ? new Date(value) : null,
  );
  const [timeValue, setTimeValue] = useState(
    value ? format(new Date(value), 'HH:mm') : '',
  );
  const previousValueRef = useRef(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const {isPC} = useMediaQuery();

  useEffect(() => {
    setInputText(formatDisplayValue(value, withTime));
    setCalendarDate(value ? new Date(value) : null);
    setTimeValue(value ? format(new Date(value), 'HH:mm') : '');
    previousValueRef.current = value;
  }, [value, withTime]);

  /**
   * 日付を更新してonSaveを呼び出す共通処理
   */
  const updateDateAndSave = useCallback(
    (newDate: Date) => {
      const newIsoValue = newDate.toISOString();
      setInputText(formatDisplayValue(newIsoValue, withTime));
      setCalendarDate(newDate);
      setTimeValue(format(newDate, 'HH:mm'));

      if (newIsoValue !== previousValueRef.current) {
        onSave(newIsoValue);
        previousValueRef.current = newIsoValue;
      }
    },
    [withTime, onSave],
  );

  const applyTextInput = useCallback(() => {
    const parsed = parseDateString(inputText, withTime);
    if (parsed) {
      const newValue = parsed.toISOString();
      if (newValue !== previousValueRef.current) {
        onSave(newValue);
        previousValueRef.current = newValue;
      }
      setInputText(formatDisplayValue(newValue, withTime));
      setCalendarDate(parsed);
      setTimeValue(format(parsed, 'HH:mm'));
    } else if (inputText === '') {
      if (previousValueRef.current !== undefined) {
        onSave(undefined);
        previousValueRef.current = undefined;
      }
      setCalendarDate(null);
      setTimeValue('');
    } else {
      // パースに失敗した場合は元の値に戻す
      setInputText(formatDisplayValue(value, withTime));
    }
  }, [inputText, withTime, onSave, value]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputText(e.target.value);
    },
    [],
  );

  const handleInputBlur = useCallback(() => {
    // カレンダーが開いている場合はblur処理をスキップ
    if (calendarOpened) return;
    applyTextInput();
  }, [calendarOpened, applyTextInput]);

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        applyTextInput();
        setCalendarOpened(false);
      }
      if (e.key === 'Escape') {
        setInputText(formatDisplayValue(value, withTime));
        setCalendarOpened(false);
      }
    },
    [applyTextInput, value, withTime],
  );

  const handleInputMouseDown = useCallback(() => {
    setCalendarOpened(!calendarOpened);
  }, [calendarOpened]);

  const handleCalendarSelect = useCallback(
    (date: Date) => {
      let newDate = date;

      // 既存の時刻を維持
      if (timeValue) {
        const {hours, minutes} = parseTimeString(timeValue);
        newDate = setMinutes(setHours(date, hours), minutes);
      }

      updateDateAndSave(newDate);
    },
    [timeValue, updateDateAndSave],
  );

  const handleTimeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newTime = e.target.value;
      setTimeValue(newTime);

      if (calendarDate && newTime) {
        const {hours, minutes} = parseTimeString(newTime);
        const newDate = setMinutes(setHours(calendarDate, hours), minutes);

        updateDateAndSave(newDate);
      }
    },
    [calendarDate, updateDateAndSave],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSave(undefined);
      setInputText('');
      setCalendarDate(null);
      setTimeValue('');
      previousValueRef.current = undefined;
    },
    [onSave],
  );

  const handlePopoverClose = useCallback(() => {
    setCalendarOpened(false);
    applyTextInput();
  }, [applyTextInput]);

  return (
    <ModalDateInputPresenter
      inputText={inputText}
      calendarOpened={calendarOpened}
      calendarDate={calendarDate}
      timeValue={timeValue}
      withTime={withTime}
      disabled={disabled}
      error={error}
      showClearButton={!!value && !disabled}
      className={className}
      readOnly={!isPC}
      onInputChange={handleInputChange}
      onInputBlur={handleInputBlur}
      onInputKeyDown={handleInputKeyDown}
      onInputMouseDown={handleInputMouseDown}
      onCalendarSelect={handleCalendarSelect}
      onTimeChange={handleTimeChange}
      onClear={handleClear}
      onPopoverClose={handlePopoverClose}
      inputRef={inputRef}
    />
  );
};

// 後方互換性のためのエイリアス
export const ModalDateInput = ModalDateInputContainer;
